import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, Loader2, Save, FileDown, FileSpreadsheet, ChevronDown, ChevronRight } from "lucide-react";
import api, { formatMoney, formatMoney2, formatNumber, formatApiErrorDetail } from "../lib/api";

const num = (v) => Number(String(v).replace(",", ".")) || 0;
const pctTxt = (v) => `${String(v).replace(".", ",")} %`;
const fecha = (v) => (v ? new Date(v).toLocaleDateString("es-ES") : "—");

function PctInput({ value, onChange, testid }) {
  return (
    <div className="relative">
      <input className="input text-right" style={{ width: 84, paddingRight: 26 }} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span>
    </div>
  );
}

function Detalle({ e }) {
  return (
    <tr><td colSpan={6} className="bg-slate-50 p-0">
      <table className="dense w-full" data-testid={`inc-parado-detalle-${e.vendedor}`}>
        <thead><tr><th>CN</th><th>Producto</th><th>Antigüedad</th><th>Últ. entrada</th><th className="num">Uds</th><th className="num">Vendido PVP</th><th className="num">%</th><th className="num">Incentivo</th></tr></thead>
        <tbody>{e.productos.map((p) => (
          <tr key={p.cn}><td>{p.cn}</td><td className="max-w-xs truncate" title={p.descripcion}>{p.descripcion}</td>
            <td><span className={"text-[10px] font-bold px-2 py-0.5 rounded-full " + (p.tramo === "2" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700")}>{p.tramo === "2" ? "+2 años" : "1-2 años"}</span></td>
            <td>{fecha(p.u_entrada)}</td><td className="num">{formatNumber(p.uds)}</td><td className="num">{formatMoney2(p.facturacion)}</td>
            <td className="num">{pctTxt(p.pct)}</td><td className="num font-semibold text-emerald-700">{formatMoney2(p.incentivo)}</td></tr>
        ))}</tbody>
      </table>
    </td></tr>
  );
}

function Tabla({ data }) {
  const [ver, setVer] = useState(null);
  if (!data.empleados.length) return <p className="text-sm text-slate-500" data-testid="inc-parado-vacio">{data.tiene_estudio ? "Este mes nadie ha vendido stock parado de más de 1 año." : "Este mes de ventas no tiene un estudio de stock de referencia."}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="dense w-full" data-testid="inc-parado-tabla">
        <thead><tr><th>Empleado</th><th className="num">Vendido 1-2 años</th><th className="num">Vendido +2 años</th><th className="num">Uds</th><th className="num">Productos</th><th className="num">Incentivo</th></tr></thead>
        <tbody>{data.empleados.map((e, i) => (
          <React.Fragment key={e.vendedor}>
            <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setVer(ver === e.vendedor ? null : e.vendedor)} data-testid={`inc-parado-fila-${i}`}>
              <td className="font-semibold"><span className="inline-flex items-center gap-1">{ver === e.vendedor ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{e.nombre}</span></td>
              <td className="num">{formatMoney2(e.vendido_1)}</td><td className="num">{formatMoney2(e.vendido_2)}</td>
              <td className="num">{formatNumber(e.uds)}</td><td className="num">{formatNumber(e.productos.length)}</td>
              <td className="num font-bold text-emerald-700" data-testid={`inc-parado-incentivo-${i}`}>{formatMoney2(e.incentivo)}</td>
            </tr>
            {ver === e.vendedor && <Detalle e={e} />}
          </React.Fragment>
        ))}</tbody>
      </table>
    </div>
  );
}

export function IncentivoParado() {
  const [imports, setImports] = useState([]);
  const [importId, setImportId] = useState("");
  const [data, setData] = useState(null);
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [busy, setBusy] = useState("");
  const load = (id) => api.get("/incentivo-parado", { params: id ? { import_id: id } : {} })
    .then((r) => { setData(r.data); setImportId(r.data.import_id); setP1(String(r.data.pct_1)); setP2(String(r.data.pct_2)); }).catch(() => setData(null));
  useEffect(() => {
    load();
    api.get("/sales/imports").then((r) => setImports(r.data.filter((d) => d.date_from))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (ev) => {
    ev.preventDefault();
    setBusy("save");
    try {
      await api.put("/incentivo-parado/settings", { pct_1: num(p1), pct_2: num(p2) });
      toast.success("Incentivo por stock parado guardado");
      await load(importId);
    } catch (err) { toast.error(err.response?.status === 422 ? "Cada % debe estar entre 0 y 50" : formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar"); }
    finally { setBusy(""); }
  };
  const bajar = async (tipo) => {
    setBusy(tipo);
    try {
      const r = await api.get(`/incentivo-parado/${tipo}`, { params: { import_id: importId }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `incentivo_stock_parado.${tipo === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
    } catch { toast.error("No se pudo descargar"); }
    finally { setBusy(""); }
  };

  if (!data) return null;
  return (
    <div className="card p-5 col-span-full" id="ind-incentivo-parado" data-testid="incentivo-parado">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
        <div className="max-w-xl">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Award size={12} /> Incentivo individual · aparte del plan general</div>
          <h3 className="font-display text-xl font-bold">Incentivo por quitarse stock parado</h3>
          <p className="text-xs text-slate-500 mt-1">Cada empleado cobra el % que elijas sobre lo que vende <b>a PVP</b> de stock D real (sin ventas y con la última entrada hace más de 1 año). Puedes premiar más el stock de más de 2 años.</p>
        </div>
        <form onSubmit={save} className="flex items-end gap-3 flex-wrap">
          <label className="text-[11px] text-slate-500 font-semibold">1-2 años<PctInput value={p1} onChange={setP1} testid="inc-parado-pct-1" /></label>
          <label className="text-[11px] text-slate-500 font-semibold">Más de 2 años<PctInput value={p2} onChange={setP2} testid="inc-parado-pct-2" /></label>
          <button className="btn btn-primary" disabled={!!busy} data-testid="inc-parado-guardar">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar</button>
        </form>
      </div>
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <select className="input" style={{ width: "auto" }} value={importId} onChange={(e) => load(e.target.value)} data-testid="inc-parado-mes">
          {imports.map((m) => <option key={m.id} value={m.id}>{m.label || m.date_from}</option>)}
        </select>
        <button className="btn btn-outline" onClick={() => bajar("pdf")} disabled={!!busy} data-testid="inc-parado-pdf">{busy === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} PDF</button>
        <button className="btn btn-outline" onClick={() => bajar("excel")} disabled={!!busy} data-testid="inc-parado-excel">{busy === "excel" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Excel</button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="rounded-lg bg-amber-50 p-3"><div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Vendido 1-2 años · {pctTxt(data.pct_1)}</div><div className="font-display text-xl font-extrabold" data-testid="inc-parado-total-1">{formatMoney(data.total_vendido_1)}</div></div>
        <div className="rounded-lg bg-red-50 p-3"><div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Vendido +2 años · {pctTxt(data.pct_2)}</div><div className="font-display text-xl font-extrabold" data-testid="inc-parado-total-2">{formatMoney(data.total_vendido_2)}</div></div>
        <div className="rounded-lg bg-slate-50 p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Empleados con incentivo</div><div className="font-display text-xl font-extrabold">{data.empleados.length}</div></div>
        <div className="rounded-lg bg-emerald-50 p-3"><div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Total a repartir</div><div className="font-display text-xl font-extrabold text-emerald-700" data-testid="inc-parado-total">{formatMoney2(data.total_incentivo)}</div></div>
      </div>
      <Tabla data={data} />
    </div>
  );
}
